/* eslint-disable max-lines-per-function */
import { createGlobalState } from 'react-use';
import { useCallback } from 'react';

import { AiAssistantRecordingGlobalState } from './useAiAssistantRecordingGlobalState.types';

const MS_IN_SECOND = 1000;

const useGlobalState = createGlobalState<AiAssistantRecordingGlobalState>({ type: 'idle' });

const getSessionSeconds = (sessionStartedAt: number): number =>
   Math.floor((Date.now() - sessionStartedAt) / MS_IN_SECOND);

type ResetCallback = VoidFunction;
type StartRecordingCallback = VoidFunction;
type StartMobileRecordingCallback = VoidFunction;
type ResetMobileRecordingCallback = VoidFunction;
type PauseRecordingCallback = VoidFunction;
type ResumeRecordingCallback = VoidFunction;
type GenerateNoteCallback = VoidFunction;
type GenerateMobileNoteCallback = VoidFunction;
type UpdateMobileNoteDurationCallback = (accumulatedSeconds: number) => void;
type CompleteGenerationCallback = VoidFunction;
type FailGenerationCallback = VoidFunction;

type UseAiAssistantRecordingGlobalStateReturnFn = {
   state: AiAssistantRecordingGlobalState;
   reset: ResetCallback;
   startRecording: StartRecordingCallback;
   startMobileRecording: StartMobileRecordingCallback;
   resetMobileRecording: ResetMobileRecordingCallback;
   pauseRecording: PauseRecordingCallback;
   resumeRecording: ResumeRecordingCallback;
   generateNote: GenerateNoteCallback;
   generateMobileNote: GenerateMobileNoteCallback;
   updateMobileNoteDuration: UpdateMobileNoteDurationCallback;
   completeGeneration: CompleteGenerationCallback;
   failGeneration: FailGenerationCallback;
};

export const useAiAssistantRecordingGlobalState = (): UseAiAssistantRecordingGlobalStateReturnFn => {
   const [state, setState] = useGlobalState();

   const reset = useCallback<ResetCallback>(() => {
      setState({ type: 'idle' });
   }, [setState]);

   const startRecording = useCallback<StartRecordingCallback>(() => {
      setState((prevState) => {
         if (prevState.type !== 'idle' && prevState.type !== 'final') {
            return prevState;
         }

         return {
            type: 'recording',
            accumulatedSeconds: 0,
            sessionStartedAt: Date.now(),
         };
      });
   }, [setState]);

   const startMobileRecording = useCallback<StartMobileRecordingCallback>(() => {
      setState((prevState) => {
         if (prevState.type !== 'idle' && prevState.type !== 'final') {
            return prevState;
         }

         return { type: 'mobileRecording' };
      });
   }, [setState]);

   const resetMobileRecording = useCallback<ResetMobileRecordingCallback>(() => {
      setState((prevState) => {
         if (prevState.type !== 'mobileRecording' && prevState.type !== 'mobilePending') {
            return prevState;
         }

         return { type: 'idle' };
      });
   }, [setState]);

   const pauseRecording = useCallback<PauseRecordingCallback>(() => {
      setState((prevState) => {
         if (prevState.type !== 'recording') {
            return prevState;
         }

         return {
            type: 'paused',
            accumulatedSeconds: prevState.accumulatedSeconds + getSessionSeconds(prevState.sessionStartedAt),
         };
      });
   }, [setState]);

   const resumeRecording = useCallback<ResumeRecordingCallback>(() => {
      setState((prevState) => {
         if (prevState.type !== 'paused' && prevState.type !== 'pending') {
            return prevState;
         }

         return {
            type: 'recording',
            accumulatedSeconds: prevState.accumulatedSeconds,
            sessionStartedAt: Date.now(),
         };
      });
   }, [setState]);

   const generateNote = useCallback<GenerateNoteCallback>(() => {
      setState((prevState) => {
         if (prevState.type === 'recording') {
            return {
               type: 'pending',
               accumulatedSeconds: prevState.accumulatedSeconds + getSessionSeconds(prevState.sessionStartedAt),
            };
         }

         if (prevState.type === 'paused') {
            return {
               type: 'pending',
               accumulatedSeconds: prevState.accumulatedSeconds,
            };
         }

         return prevState;
      });
   }, [setState]);

   const generateMobileNote = useCallback<GenerateMobileNoteCallback>(() => {
      setState((prevState) => {
         if (prevState.type !== 'mobileRecording') {
            return prevState;
         }

         return { type: 'mobilePending', accumulatedSeconds: 0 };
      });
   }, [setState]);

   const updateMobileNoteDuration = useCallback<UpdateMobileNoteDurationCallback>(
      (accumulatedSeconds) => {
         setState((prevState) => {
            if (prevState.type !== 'mobilePending') {
               return prevState;
            }

            return { type: 'mobilePending', accumulatedSeconds: accumulatedSeconds };
         });
      },
      [setState]
   );

   const completeGeneration = useCallback<CompleteGenerationCallback>(() => {
      setState((prevState) => {
         if (prevState.type !== 'pending' && prevState.type !== 'mobilePending') {
            return prevState;
         }

         return {
            type: 'final',
         };
      });
   }, [setState]);

   const failGeneration = useCallback<FailGenerationCallback>(() => {
      setState((prevState) => {
         if (prevState.type === 'pending') {
            return {
               type: 'paused',
               accumulatedSeconds: prevState.accumulatedSeconds,
            };
         }

         if (prevState.type === 'mobilePending') {
            return { type: 'mobileRecording' };
         }

         return prevState;
      });
   }, [setState]);

   return {
      state: state,
      reset: reset,
      startRecording: startRecording,
      startMobileRecording: startMobileRecording,
      resetMobileRecording: resetMobileRecording,
      pauseRecording: pauseRecording,
      resumeRecording: resumeRecording,
      generateNote: generateNote,
      generateMobileNote: generateMobileNote,
      updateMobileNoteDuration: updateMobileNoteDuration,
      completeGeneration: completeGeneration,
      failGeneration: failGeneration,
   };
};
